import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

const VideoRecorder = forwardRef(({ teamNames, set }, ref) => {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [chunks, setChunks] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [startTime, setStartTime] = useState(null);

  // List available video input devices
  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: true }).then(() => {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        setDevices(devices.filter((d) => d.kind === "videoinput"));
      });
    });
  }, []);

  // Start video preview when device is selected
  useEffect(() => {
    if (selectedDeviceId) {
      navigator.mediaDevices
        .getUserMedia({ video: { deviceId: selectedDeviceId } })
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        });
    }
  }, [selectedDeviceId]);

  // Start recording
  const startRecording = () => {
    // Libera o URL antigo, se houver
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
      setVideoUrl(null);
    }
    setChunks([]);
    setIsRecording(true);
    const stream = videoRef.current.srcObject;
    const mediaRecorder = new window.MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    let localChunks = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) localChunks.push(e.data);
    };
    mediaRecorder.onstop = () => {
      const blob = new Blob(localChunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
      setChunks([]); // Limpa os chunks globais
    };
    mediaRecorder.start();
    setStartTime(Date.now());
  };

  // Stop recording
  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Exponha o método getCurrentTime para o pai
  useImperativeHandle(ref, () => ({
    getCurrentTime: () => {
      if (!isRecording || !startTime) return 0;
      return (Date.now() - startTime) / 1000; // segundos com casas decimais
    },
  }));

  // Montar nome do arquivo
  const getFileName = () => {
    const home = teamNames?.home?.name?.replace(/\s+/g, "_") || "home";
    const away = teamNames?.away?.name?.replace(/\s+/g, "_") || "away";
    const setStr = set ? `_set_${set}` : "";
    return `${home}-${away}${setStr}.webm`;
  };

  return (
    <div
      className="border rounded p-4 bg-gray-50"
      style={{ width: 960, height: 540 }}
    >
      <div className="mb-2">
        <label>Câmera: </label>
        <select
          value={selectedDeviceId}
          onChange={(e) => setSelectedDeviceId(e.target.value)}
          disabled={isRecording}
        >
          <option value="">Selecione</option>
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || `Camera ${d.deviceId}`}
            </option>
          ))}
        </select>
      </div>
      <video
        ref={videoRef}
        autoPlay
        muted
        width={960}
        height={540}
        style={{ background: "#222" }}
      />
      <div className="mt-2">
        {!isRecording ? (
          <button
            onClick={startRecording}
            disabled={!selectedDeviceId}
            className="bg-blue-500 text-white px-4 py-2 rounded mr-2"
          >
            Iniciar Gravação
          </button>
        ) : (
          <button
            onClick={stopRecording}
            className="bg-red-500 text-white px-4 py-2 rounded"
          >
            Parar Gravação
          </button>
        )}
      </div>
      {videoUrl && (
        <div className="mt-2">
          <a
            href={videoUrl}
            download={getFileName()}
            className="text-blue-700 underline"
          >
            Baixar vídeo gravado
          </a>
        </div>
      )}
    </div>
  );
});

export default VideoRecorder;
